import { SelectListItemRenderer } from '@acies/core';
import { theme } from '@acies/ui';
import type { IGeometryShapeType } from '../../../domain/geometry-shape-types';
import GeometrySVGShape from './geometry-svg-shape';

/** Every state the tile dresses itself for: the styled state, so hovering is not among them. */
type TileState = GeometrySelectListItemRenderer['styledState'];

/**
 * Whether a tile that is not selected looks selected, state by state.
 *
 * Reaching one with the keyboard shows what picking it would look like; pressing it takes that back until the press lets go and the pick is made. Hovering one shows nothing, since the cursor is what says it can be picked. Written out per state, so each can be read against what was asked for and the compiler asks for
 * an answer the day a state is added.
 */
const LOOKS_SELECTED: Readonly<Record<TileState, boolean>> = {
	disabled: false,
	focused: true,
	focusedPressed: false,
	idle: false,
	pressed: false,
};

/**
 * One shape in the geometry dialog, for the user to pick.
 *
 * A square tile with a soft stroke, the same size for every shape so the grid lines up, with a drawing of the shape it is handed in the middle of it.
 *
 * Selected, it is marked with the focus stroke and fill, and its shape drawn in the focus colour, whatever the pointer does, and offers no pointer: the list is never left without a selection, so pressing the selected tile does nothing — see `GeometrySelectList`.
 */
export default class GeometrySelectListItemRenderer extends SelectListItemRenderer<IGeometryShapeType> {
	#geometrySVGShape!: GeometrySVGShape;
	public constructor() {
		super();
		this.width = 72;
		this.height = 72;
		this.cornerRadius = 4;
		this.stroke = theme.strokes.soft;
		this.cursor = 'pointer';
		this.alignment = 'center';
		this.paddingHorizontal = 4;
		this.paddingVertical = 4;
	}

	/** The drawing of the shape the tile is handed. */
	public get geometrySVGShape(): GeometrySVGShape {
		if (!this.#geometrySVGShape) {
			this.#geometrySVGShape = new GeometrySVGShape();
		}
		return this.#geometrySVGShape;
	}

	protected override initialize(): void {
		super.initialize();
		this.addComponent(this.geometrySVGShape);
	}

	/** Draws the shape the tile is now handed, which is none for a tile in the pool waiting to be handed one. */
	protected override itemChanged(): void {
		super.itemChanged();
		this.geometrySVGShape.shapeType = this.item;
	}

	protected override selectedChanged(): void {
		super.selectedChanged();
		this.#updateStyles();
	}

	protected override stateChanged(): void {
		super.stateChanged();
		this.#updateStyles();
	}

	/** Dresses the tile and the drawing in it for whether it is selected and the state it is in. */
	#updateStyles(): void {
		const looksSelected = this.selected || LOOKS_SELECTED[this.styledState];
		this.stroke = looksSelected ? theme.strokes.focus : theme.strokes.soft;
		this.fill = looksSelected ? theme.colors.fill.focus : null;
		this.cursor = this.selected || this.state === 'disabled' ? 'default' : 'pointer';
		this.geometrySVGShape.strokeColor = looksSelected ? theme.colors.icon.focused : theme.colors.icon.soft;
	}
}
customElements.define('cf-geometry-select-list-item-renderer', GeometrySelectListItemRenderer);
